import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../lib/formatters.js';

interface SettlementLine {
  id: string;
  claimId: string;
  claimNumber: string;
  claimType: string;
  distributorName: string;
  amount: number;
  isoCurrencyCode: string;
  status: string;
  bankReference?: string | null;
}

interface Props {
  lines: SettlementLine[];
}

const LINE_STATUS_COLORS: Record<string, string> = {
  QUEUED: 'bg-copper-soft text-copper-deep',
  INITIATED: 'bg-copper-soft text-copper',
  COMPLETED: 'bg-success/10 text-success',
  FAILED: 'bg-danger/10 text-danger',
};

export function SettlementPreviewTable({ lines }: Props) {
  const { t } = useTranslation();

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-slate-500 border-b border-hairline">
            <th className="text-left py-2 pr-4">{t('settlement.table.claimNumber')}</th>
            <th className="text-left py-2 pr-4">{t('settlement.table.distributor')}</th>
            <th className="text-left py-2 pr-4">{t('settlement.table.claimType')}</th>
            <th className="text-right py-2 pr-4">{t('settlement.table.amount')}</th>
            <th className="text-left py-2">{t('settlement.table.status')}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.id} className="border-b border-hairline last:border-0">
              <td className="py-2 pr-4 font-mono text-xs text-copper">{line.claimNumber}</td>
              <td className="py-2 pr-4 text-xs text-slate-600">{line.distributorName}</td>
              <td className="py-2 pr-4 text-xs">
                {t(`wizard.claimTypes.${line.claimType}.label`, { defaultValue: line.claimType })}
              </td>
              <td className="py-2 pr-4 text-right font-mono text-xs">{formatCurrency(line.amount, line.isoCurrencyCode)}</td>
              <td className="py-2">
                <span className={['text-xs px-1.5 py-0.5 rounded-sm font-medium', LINE_STATUS_COLORS[line.status] ?? 'bg-slate-100 text-slate-600'].join(' ')}>
                  {t(`settlement.lineStatus.${line.status}`, { defaultValue: line.status })}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
